// Helpers
import { withCDN } from 'helpers/cdn';

export type SportLandingFeature = {
  title: string;
  text: string;
  image: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
};

export type SportLandingMerchProduct = {
  image: string;
  name: string;
  athlete: string;
  price: number;
};

export type SportLandingFaqItem = {
  question: string;
  answer: string;
};

export type SportLandingConfig = {
  path: string;
  seo: {
    title: string;
    description: string;
    schemaName: string;
  };
  hero: {
    title: string;
    subtitle: string;
    image: string;
    imageAlt: string;
  };
  howItWorks: {
    title: string;
    intro: string;
    steps: { title: string; text: string }[];
  };
  personalVideos: SportLandingFeature;
  stream: SportLandingFeature;
  broadcast: SportLandingFeature;
  merch: {
    title: string;
    text: string;
    products: SportLandingMerchProduct[];
  };
  onePageLink: {
    title: string;
    text: string;
  };
  faq: SportLandingFaqItem[];
};

const buildFaq = (fighterNoun: string): SportLandingFaqItem[] => [
  {
    question: `How do I set up my ${fighterNoun} profile on MILLIONS?`,
    answer:
      'Sign up, add your photo and bio, and choose what you want to sell: Personal Videos, Streams, merch or all three. You can launch them together or one at a time, and our Athlete Success team can walk you through it.',
  },
  {
    question: `Is there a cost for ${fighterNoun}s to join MILLIONS?`,
    answer:
      'No. Joining, creating a profile, launching merch, offering Personal Videos and streaming are all free. MILLIONS only earns when you make a sale: a platform fee, currently 20%, so you keep 80%.',
  },
  {
    question: 'How do I get paid?',
    answer:
      'Payouts are sent monthly, typically around the 15th of each month.',
  },
  {
    question: 'Can I offer free Personal Videos or Streams?',
    answer:
      'Yes for live streams: switch the stream to free when you set it up. Pay-per-view events always have a ticket price. For a free Personal Video giveaway, contact Athlete Success and we can look at sponsoring it from our marketing budget.',
  },
  {
    question: 'I don’t have merch designs. Can you help?',
    answer:
      'Yes. Contact Athlete Success and we’ll help you create designs and a brand your fans will love.',
  },
];

const howItWorksSteps = [
  {
    title: 'Register your account',
    text:
      'Upload your picture and a short bio, then pick the products you want to offer. We handle billing and transactions and bring you new customers.',
  },
  {
    title: 'Build your brand',
    text:
      'We’ll help you build your brand and designs if you don’t have them yet. If you do, you’re ready to launch.',
  },
  {
    title: 'Promote your page',
    text:
      'Share your MILLIONS page with your audience on every channel. Launch Streams any time, receive Personal Video requests and drop new merch for upcoming fights or trending moments.',
  },
];

const streamText =
  'Watch fights live with your fans on a Stream and broadcast to MILLIONS of viewers whenever it suits you. We automate the whole process end to end. You set the price and start the broadcast.';

const merchText =
  'Whether you have an existing line or want help developing a new collection, we make it easy. Launch new designs as often as you like. No inventory, no billing hassle. Just launch and sell.';

const personalVideosText =
  'Let fans request short Personal Videos from you: fight predictions, training tips, your story or anything else. Create shareable content while growing engagement with your current fans and reaching new ones.';

export const SPORT_LANDINGS: Record<'boxing' | 'mma', SportLandingConfig> = {
  boxing: {
    path: '/boxing',
    seo: {
      title: 'Make Money as a Boxer: Videos, Streams & Merch | MILLIONS',
      description:
        'MILLIONS helps boxers build their brand and earn from fans with paid Personal Videos, live and pay-per-view Streams, and custom merch. Free to join.',
      schemaName: 'MILLIONS for boxers',
    },
    hero: {
      title: 'Every boxer is also an entrepreneur',
      subtitle:
        'Build your boxing brand on MILLIONS with paid Personal Videos, live Streams and exclusive custom merch, all on a single platform.',
      image: withCDN(
        '/images/recruitment/boxing/recruitment_bell_hero_bg_tiny.png'
      ),
      imageAlt: 'Boxer throwing a jab during a fight',
    },
    howItWorks: {
      title: 'How MILLIONS works for boxers',
      intro:
        'Getting started is as simple as creating a social media profile. Start selling Personal Videos, Streams and merch within minutes.',
      steps: howItWorksSteps,
    },
    personalVideos: {
      title: 'Paid Personal Videos for boxing fans',
      text: personalVideosText,
      image: withCDN(
        '/images/recruitment/boxing/albert_bell_paid_video_amas.png'
      ),
      imageAlt: 'Boxer recording a Personal Video for a fan',
      imageWidth: 671,
      imageHeight: 780,
    },
    stream: {
      title: 'Live Streams with your boxing fans',
      text: streamText,
      image: withCDN('/images/recruitment/boxing/albert_bell_watch_stream.png'),
      imageAlt: 'Boxer hosting a live Stream for fans',
      imageWidth: 400,
      imageHeight: 560,
    },
    broadcast: {
      title: 'Broadcast boxing events to the world',
      text:
        'Build and launch a pay-per-view Stream for any boxing event. Upload a poster or use a template and start the broadcast, then promote your other products during the Stream.',
      image: withCDN('/images/recruitment/boxing/davis_cruz.png'),
      imageAlt: 'Pay-per-view boxing event poster',
      imageWidth: 1012,
      imageHeight: 1012,
    },
    merch: {
      title: 'Design, launch and sell boxing merch in minutes',
      text: merchText,
      products: [
        {
          image: withCDN('/images/recruitment/boxing/merch/cap_bell.png'),
          name: 'Cap / Albert Bell',
          athlete: 'Albert Bell',
          price: 12.99,
        },
        {
          image: withCDN('/images/recruitment/boxing/merch/hoodie_bell.png'),
          name: 'Hoodie / Albert Bell',
          athlete: 'Albert Bell',
          price: 49.99,
        },
        {
          image: withCDN('/images/recruitment/boxing/merch/t-shirt_bell.png'),
          name: 'T-Shirt / Albert Bell',
          athlete: 'Albert Bell',
          price: 29.99,
        },
      ],
    },
    onePageLink: {
      title: 'All your products, one page, one link',
      text:
        'When you join MILLIONS, your Personal Videos, Streams and merch live on one page with a single link. Your page is built to drive engagement and turn your biggest boxing fans into paying customers.',
    },
    faq: buildFaq('boxer'),
  },
  mma: {
    path: '/mma',
    seo: {
      title: 'MMA Fighters: Earn with Videos, Streams & Merch | MILLIONS',
      description:
        'MILLIONS helps MMA fighters build their brand and earn from fans with paid Personal Videos, live and pay-per-view Streams, and custom merch. Free to join.',
      schemaName: 'MILLIONS for MMA fighters',
    },
    hero: {
      title: 'Every MMA fighter is also an entrepreneur',
      subtitle:
        'Build your MMA brand on MILLIONS with paid Personal Videos, live Streams and exclusive custom merch, all on a single platform.',
      image: withCDN(
        '/images/recruitment/mma/recruitment_pierre_hero_bg_tiny.png'
      ),
      imageAlt: 'MMA fighter standing in the cage after a fight',
    },
    howItWorks: {
      title: 'How MILLIONS works for MMA fighters',
      intro:
        'Getting started is as simple as creating a social media profile. Start selling Personal Videos, Streams and merch within minutes.',
      steps: howItWorksSteps,
    },
    personalVideos: {
      title: 'Paid Personal Videos for MMA fans',
      text: personalVideosText,
      image: withCDN('/images/recruitment/mma/st_pierre_paid_video_amas.png'),
      imageAlt: 'MMA fighter recording a Personal Video for a fan',
      imageWidth: 672,
      imageHeight: 780,
    },
    stream: {
      title: 'Live Streams with your MMA fans',
      text: streamText,
      image: withCDN('/images/recruitment/mma/st_pierre_watch_stream.png'),
      imageAlt: 'MMA fighter hosting a live Stream for fans',
      imageWidth: 400,
      imageHeight: 560,
    },
    broadcast: {
      title: 'Broadcast MMA events to the world',
      text:
        'Build and launch a pay-per-view Stream for any MMA event. Upload a poster or use a template and start the broadcast, then promote your other products during the Stream.',
      image: withCDN('/images/recruitment/mma/mayweather_mc-gregor.png'),
      imageAlt: 'Pay-per-view fight event poster',
      imageWidth: 1012,
      imageHeight: 1012,
    },
    merch: {
      title: 'Design, launch and sell MMA merch in minutes',
      text: merchText,
      products: [
        {
          image: withCDN('/images/recruitment/mma/merch/cap_st_pierre.png'),
          name: 'Cap / Georges St-Pierre',
          athlete: 'George St.Pierre',
          price: 12.99,
        },
        {
          image: withCDN('/images/recruitment/mma/merch/hoodie_st_pierre.png'),
          name: 'Hoodie / Georges St-Pierre',
          athlete: 'George St.Pierre',
          price: 49.99,
        },
        {
          image: withCDN('/images/recruitment/mma/merch/t-shirt_st_pierre.png'),
          name: 'T shirt / Georges St-Pierre',
          athlete: 'George St.Pierre',
          price: 29.99,
        },
      ],
    },
    onePageLink: {
      title: 'All your products, one page, one link',
      text:
        'When you join MILLIONS, your Personal Videos, Streams and merch live on one page with a single link. Your page is built to drive engagement and turn your biggest MMA fans into paying customers.',
    },
    faq: buildFaq('MMA fighter'),
  },
};
